import React, { Suspense, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Outlet, Navigate, useParams } from 'react-router-dom';
import { SkeletonTheme } from 'react-loading-skeleton';
import Layout from './components/layout/Layout';
import NotificationContainer from './components/ui/NotificationContainer';
import ErrorBoundary from './components/ui/ErrorBoundarySimple';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-loading-skeleton/dist/skeleton.css';
import LinkedInCallbackPage from './pages/LinkedInCallback';
import { useAuthFallback as useAuth } from 'hooks/useAuthFallback';
import { OnboardingProtectedRoute } from './components/routes/OnboardingProtectedRoute';
import PageLoadingSkeleton from './components/ui/PageLoadingSkeleton';
import { SuccessModalProvider, useSuccessModalState } from './hooks/useSuccessModal';
import SuccessPopup from 'components/common/SuccessPopup';
import RealTimeCreditUpdater from 'components/payments/RealTimeCreditUpdater';
import DashboardPageSkeleton    from './components/ui/DashboardPageSkeleton';
import GenericPageSkeleton      from './components/ui/GenericPageSkeleton';
import ChatPageSkeleton         from './components/ui/ChatPageSkeleton';
import TransactionPageSkeleton  from './components/ui/TransactionPageSkeleton';
import SettingsPageSkeleton     from './components/ui/SettingsPageSkeleton';
import ProfileMediaSkeleton     from './components/ui/ProfileMediaSkeleton';
import CompanyProfileSkeleton   from './components/ui/CompanyProfileSkeleton';
import CandidatesToolbarSkeleton from './components/ui/CandidatesToolbarSkeleton';
import MatchingListSkeleton     from './components/ui/MatchingListSkeleton';
import CandidateDetailPageSkeleton from './components/ui/CandidateDetailPageSkeleton';
import LookingForPage_Direct   from 'pages/LookingFor/LookingFor';
import './index.css';
import { lazyWithRetry } from './utils/lazyWithRetry';

// ── Skeleton imports ──────────────────────────────────────────────────────────

// ── Lazy-loaded pages ─────────────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const HomePage               = lazyWithRetry(() => import('./pages/HomePage'));
const PaymentSepratePage          = lazyWithRetry(() => import('./pages/PaymentSeprate/PaymentSepratePage'));
const BusinessVerificationPage          = lazyWithRetry(() => import('./pages/BusinessVerificationPage/BusinessVerificationPage'));
const DashboardPage          = lazyWithRetry(() => import('./pages/DashboardPage'));
const ProfileDetails         = lazyWithRetry(() => import('pages/ProfileDetails/ProfileDetails'));
const CandidatesList         = lazyWithRetry(() => import('pages/CandidatesList/CandidatesList'));
const CandidateprofileDetails= lazyWithRetry(() => import('pages/CandidatesList/CandidateprofileDetails'));
const TransactionHistory     = lazyWithRetry(() => import('pages/TransactionHistory/TransactionHistory'));
const MatchingList           = lazyWithRetry(() => import('pages/MatchingList/MatchingList'));
const Chats                  = lazyWithRetry(() => import('pages/Chat/Chats'));
const ProfileView            = lazyWithRetry(() => import('pages/ProfileView/CompanyProfileView'));
const ProfileMedia           = lazyWithRetry(() => import('pages/ProfileView/ProfileMedia'));
const Pitchvideo             = lazyWithRetry(() => import('pages/ProfileView/Pitchvideo'));
const Portfolio              = lazyWithRetry(() => import('pages/ProfileView/Portfolio'));
const ProfileReviews         = lazyWithRetry(() => import('pages/ProfileView/ProfileReviews'));
const Settings               = lazyWithRetry(() => import('pages/Settings/Settings'));
const PitchRecord            = lazyWithRetry(() => import('pages/ProfileView/PitchRecord'));
const RecordYouPitch         = lazyWithRetry(() => import('pages/ProfileView/RecordYouPitch'));
const Notifications          = lazyWithRetry(() => import('pages/Notifications/Notifications'));
const LoginPage              = lazyWithRetry(() => import('./pages/onboarding/LoginPage'));
const ForgotPasswordPage     = lazyWithRetry(() => import('./pages/onboarding/ForgotPasswordPage'));
const SetPasswordPage        = lazyWithRetry(() => import('./pages/onboarding/SetPasswordPage'));
const AuthCodePage           = lazyWithRetry(() => import('./pages/onboarding/AuthCodePage'));
const BasicDetailsPage       = lazyWithRetry(() => import('./pages/onboarding/BasicDetailsPage'));
const CompanyDetailsPage     = lazyWithRetry(() => import('./pages/onboarding/CompanyDetailsPage'));
const LookingForPage         = lazyWithRetry(() => import('./pages/onboarding/LookingForPage'));
const CompanyIntroPage       = lazyWithRetry(() => import('./pages/onboarding/CompanyIntroPage'));
const Credits                = lazyWithRetry(() => import('./pages/onboarding/Credits'));
const EmailOTPPage           = lazyWithRetry(() => import('./pages/onboarding/EmailOTPPage'));
const LoginOtpPage           = lazyWithRetry(() => import('./pages/onboarding/LoginOtpPage'));
const VerificationPage       = lazyWithRetry(() => import('./pages/onboarding/VerificationPage'));
const ActivatePage           = lazyWithRetry(() => import('./pages/onboarding/ActivatePage'));
// const CreditFeaturesList     = lazyWithRetry(() => import('./pages/CreditFeaturesList'));
const PersonalProfile        = lazyWithRetry(() => import('./pages/PersonalProfile/PersonalProfile'));
const VideoCallPage          = lazyWithRetry(() => import('./pages/VideoCallPage'));

// ── Helper: creates a COMPONENT (not JSX element) that wraps a lazy page
// with its own Suspense + matching skeleton.
// Must return a component function so React creates a fresh Suspense boundary
// on every route mount — prevents stale resolved boundaries from skipping fallback.
function makePage(
  Page: React.LazyExoticComponent<any>,
  Skeleton: React.FC
): React.FC {
  return function PageWithSkeleton() {
    return (
      <Suspense fallback={<Skeleton />}>
        <Page />
      </Suspense>
    );
  };
}

// Parameter redirect helpers for backward compatibility
const CandidateDetailRedirect: React.FC = () => {
  const { id } = useParams();
  return <Navigate to={`/candidate/${id}`} replace />;
};

const JobEditRedirect: React.FC = () => {
  const { jobId } = useParams();
  return <Navigate to={`/job/edit/${jobId}`} replace />;
};

const JobDetailRedirect: React.FC = () => {
  const { jobId } = useParams();
  return <Navigate to={`/job/${jobId}`} replace />;
};

// Candidates list skeleton (toolbar + cards)
const CandidatesListSkeleton: React.FC = () => (
  <div className="candidate-list-page">
    <CandidatesToolbarSkeleton />
    <div style={{ marginTop: 16 }}>
      {[...Array(6)].map((_, i) => (
        <div
          key={i}
          className="candidate-list-card card mb-2"
          style={{ borderRadius: 12, border: '1px solid #e8edf5', padding: '14px 16px 14px 18px', position: 'relative' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1 }}>
              <div style={{ flexShrink: 0, width: 58, height: 72, position: 'relative' }}>
                <div style={{ position: 'absolute', left: 2, top: 6, width: 61, height: 61, borderRadius: '50%', background: '#e8edf5' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ height: 16, width: '55%', borderRadius: 6, background: '#e8edf5', marginBottom: 8 }} />
                <div style={{ height: 13, width: '40%', borderRadius: 6, background: '#e8edf5', marginBottom: 8 }} />
                <div style={{ height: 12, width: '70%', borderRadius: 6, background: '#e8edf5' }} />
              </div>
            </div>
            <div style={{ height: 36, width: 110, borderRadius: 999, background: '#e8edf5', flexShrink: 0 }} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ── Layout wrappers ───────────────────────────────────────────────────────────

// MainLayout — no Suspense here, each route has its own
const MainLayout: React.FC = () => (
  <ErrorBoundary>
    <Layout>
      <Outlet />
    </Layout>
  </ErrorBoundary>
);

// Onboarding layout — generic top-bar skeleton is fine for login/onboarding
const OnboardingLayout: React.FC = () => (
  <ErrorBoundary>
    <Suspense fallback={<PageLoadingSkeleton />}>
      <Outlet />
    </Suspense>
  </ErrorBoundary>
);

// Auth loading bar (used while session is being validated)
const AuthLoadingBar: React.FC = () => (
  <div
    style={{
      position: 'fixed', top: 0, left: 0, right: 0,
      height: 3, background: '#e2e8f0', zIndex: 9999, overflow: 'hidden',
    }}
  >
    <div
      style={{
        height: '100%', width: '40%',
        background: 'linear-gradient(90deg, #002781, #457EFF)',
        borderRadius: '0 2px 2px 0',
        animation: 'page-load-slide 1.2s ease-in-out infinite',
      }}
    />
  </div>
);

export const ProtectedRoute: React.FC = () => {
  const { isAuthenticated, isLoading, validateSession } = useAuth();
  useEffect(() => { validateSession(); }, [validateSession]);
  if (isLoading) return <AuthLoadingBar />;
  return isAuthenticated ? <Outlet /> : <Navigate to="/login" replace />;
};

export const PublicRoute: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <AuthLoadingBar />;
  return isAuthenticated ? (
    <Navigate to="/" replace />
  ) : (
    <Suspense fallback={<AuthLoadingBar />}>
      <Outlet />
    </Suspense>
  );
};

const ErrorPage: React.FC = () => (
  <div className="error-page">
    <div className="error-content">
      <h1>404 - Page Not Found</h1>
      <p>The page you're looking for doesn't exist.</p>
      <a href="/" className="btn btn-primary">Go Home</a>
    </div>
  </div>
);

const LinkedInRedirect: React.FC = () => <LinkedInCallbackPage />;

const ChatPage = makePage(Chats, ChatPageSkeleton);
const LoginPage_Page = makePage(LoginPage, AuthLoadingBar);
const ForgotPasswordPage_Page = makePage(ForgotPasswordPage, AuthLoadingBar);
const SetPasswordPage_Page = makePage(SetPasswordPage, AuthLoadingBar);
const LoginOtpPage_Page = makePage(LoginOtpPage, AuthLoadingBar);
const AuthCodePage_Page = makePage(AuthCodePage, AuthLoadingBar);
const BasicDetailsPage_Page = makePage(BasicDetailsPage, AuthLoadingBar);
const EmailOTPPage_Page = makePage(EmailOTPPage, AuthLoadingBar);

// ── Router ────────────────────────────────────────────────────────────────────
const router = createBrowserRouter([
  {
    path: '/',
    element: <ProtectedRoute />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <OnboardingProtectedRoute />,
        children: [
          {
            element: <MainLayout />,
            children: [
              // Each route uses a component (not JSX element) so Suspense boundary
              // is freshly mounted on every navigation — no stale resolved boundaries
              { index: true,               element: React.createElement(makePage(DashboardPage,           DashboardPageSkeleton)) },
              { path: 'profile-details',   element: React.createElement(makePage(ProfileDetails,          GenericPageSkeleton)) },
              { path: 'candidates',        element: React.createElement(makePage(CandidatesList,          CandidatesListSkeleton)) },
              { path: 'candidate/:id',     element: React.createElement(makePage(CandidateprofileDetails, CandidateDetailPageSkeleton)) },
              { path: 'billing-history',   element: React.createElement(makePage(TransactionHistory,      TransactionPageSkeleton)) },
              { path: 'connections',       element: React.createElement(makePage(MatchingList,            MatchingListSkeleton)) },
              { path: 'personal-profile',  element: React.createElement(makePage(PersonalProfile,         GenericPageSkeleton)) },
              { path: 'chats',             element: <ChatPage /> },
              { path: 'chat/:chatId',      element: <ChatPage /> },
              { path: 'company-profile',   element: React.createElement(makePage(ProfileView,             CompanyProfileSkeleton)) },
              { path: 'profile-media',     element: React.createElement(makePage(ProfileMedia,            ProfileMediaSkeleton)) },
              { path: 'pitch-video',       element: React.createElement(makePage(Pitchvideo,              GenericPageSkeleton)) },
              { path: 'portfolio',         element: React.createElement(makePage(Portfolio,               GenericPageSkeleton)) },
              { path: 'reviews',           element: React.createElement(makePage(ProfileReviews,          GenericPageSkeleton)) },
              { path: 'settings',          element: React.createElement(makePage(Settings,                SettingsPageSkeleton)) },
              { path: 'generate-pitch',    element: React.createElement(makePage(PitchRecord,             GenericPageSkeleton)) },
              { path: 'record-pitch',      element: React.createElement(makePage(RecordYouPitch,          GenericPageSkeleton)) },
              { path: 'notifications',     element: React.createElement(makePage(Notifications,           GenericPageSkeleton)) },
              { path: 'payment',           element: React.createElement(makePage(PaymentSepratePage,      GenericPageSkeleton)) },
              { path: 'business-verification', element: React.createElement(makePage(BusinessVerificationPage, GenericPageSkeleton)) },
              { path: 'jobs',               element: <LookingForPage_Direct /> },
              { path: 'job/add',           element: <LookingForPage_Direct /> },
              { path: 'job/edit/:jobId',   element: <LookingForPage_Direct /> },
              { path: 'job/:jobId',        element: <LookingForPage_Direct /> },
              // Backward-compatibility legacy URL redirects
              // { path: 'candidates/:id',    element: <CandidateDetailRedirect /> },
              // { path: 'candidateslist',    element: <Navigate to="/candidates" replace /> },
              // { path: 'matchingProfile',   element: <Navigate to="/connections" replace /> },
              // { path: 'transactionHistory',element: <Navigate to="/billing-history" replace /> },
              // { path: 'business-verification', element: <Navigate to="/business-verification" replace /> },
              // { path: 'generatePicth',     element: <Navigate to="/generate-pitch" replace /> },
              // { path: 'recordyourPitch',   element: <Navigate to="/record-pitch" replace /> },
              // { path: 'profiledetails',    element: <Navigate to="/profile-details" replace /> },
              // { path: 'profileMedia',      element: <Navigate to="/profile-media" replace /> },
              // { path: 'pitchVideo',        element: <Navigate to="/pitch-video" replace /> },
              // { path: 'allReviews',        element: <Navigate to="/reviews" replace /> },
              // { path: 'payment-page',      element: <Navigate to="/payment" replace /> },
            ],
          },
          // Full-screen Video Call (no layout wrapper)
          {
            path: 'video-call',
            element: React.createElement(makePage(VideoCallPage, GenericPageSkeleton)),
          },
        ],
      },
    ],
  },
  {
    path: '/login',
    element: <PublicRoute />,
    errorElement: <ErrorPage />,
    children: [
      { path: '', element: <LoginPage_Page /> },
    ],
  },
  {
    path: '/auth/login',
    element: <Navigate to="/login" replace />,
  },
  {
    path: '/verify-email',
    element: <PublicRoute />,
    errorElement: <ErrorPage />,
    children: [
      { path: '', element: <LoginOtpPage_Page /> },
    ],
  },
  {
    path: '/onboarding/verify-email',
    element: <PublicRoute />,
    errorElement: <ErrorPage />,
    children: [
      { path: '', element: <LoginOtpPage_Page /> },
    ],
  },
  {
    path: '/onboarding/login-otp',
    element: <Navigate to="/verify-email" replace />,
  },
  {
    path: '/login-otp',
    element: <Navigate to="/verify-email" replace />,
  },
  {
    path: '/forgot-password',
    element: <PublicRoute />,
    errorElement: <ErrorPage />,
    children: [
      { path: '', element: <ForgotPasswordPage_Page /> },
    ],
  },
  {
    path: '/onboarding/forgot-password',
    element: <Navigate to="/forgot-password" replace />,
  },
  {
    path: '/set-password',
    element: <PublicRoute />,
    errorElement: <ErrorPage />,
    children: [
      { path: '', element: <SetPasswordPage_Page /> },
    ],
  },
  {
    path: '/auth-code',
    element: <PublicRoute />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <OnboardingLayout />,
        children: [{ path: '', element: <AuthCodePage_Page /> }],
      },
    ],
  },
  {
    path: 'technbuddy/callback',
    element: <LinkedInRedirect />,
  },
  {
    path: '/onboarding',
    errorElement: <ErrorPage />,
    element: <OnboardingLayout />,
    children: [
      { path: 'basic-details', element: <BasicDetailsPage_Page /> },
      { path: 'email-otp',     element: <EmailOTPPage_Page /> },
      {
        path: '',
        element: <ProtectedRoute />,
        children: [
          { path: 'company-details', element: <CompanyDetailsPage /> },
          { path: 'company-intro',   element: <CompanyIntroPage /> },
          { path: 'verification',    element: <VerificationPage /> },
          { path: 'activate',        element: <ActivatePage /> },
          { path: 'credits',         element: <Credits /> },
        ],
      },
    ],
  },
]);

// ── App root ──────────────────────────────────────────────────────────────────
const GlobalSuccessModal: React.FC = () => {
  const { state, hide } = useSuccessModalState();
  return (
    <SuccessPopup
      show={state.show}
      onClose={hide}
      title={state.title}
      message={state.message}
      buttonText={state.buttonText}
    />
  );
};

const App: React.FC = () => (
  <ErrorBoundary>
    <SkeletonTheme baseColor="#e2e8f0" highlightColor="#f1f5f9">
      <SuccessModalProvider>
        <RealTimeCreditUpdater>
          <div className="App">
            <RouterProvider router={router} />
            <NotificationContainer />
            <GlobalSuccessModal />
          </div>
        </RealTimeCreditUpdater>
      </SuccessModalProvider>
    </SkeletonTheme>
  </ErrorBoundary>
);

export default App;
